import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes } from "react";
import { cn } from "@/lib/utils";
import { Spinner } from "./cargando";

/**
 * El icono lo dimensiona el boton (`[&_svg]:size-4`, `3.5` en `sm`/`icon-sm`)
 * y el `gap-2` separa del texto: el feature escribe `<Button><Plus />Cargar</Button>`
 * sin `h-4 w-4` ni `mr-2`.
 */
const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50 aria-busy:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary/90",
        destructive: "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline: "border border-input bg-background hover:bg-accent hover:text-accent-foreground",
        secondary: "bg-secondary text-secondary-foreground hover:bg-secondary/80",
        ghost: "hover:bg-accent hover:text-accent-foreground",
        // Accion destructiva secundaria ("Anular la orden", "Descartar"): en rojo
        // pero sin fondo, para que no compita con la primaria del footer.
        "ghost-destructive": "text-destructive hover:bg-destructive/10 hover:text-destructive",
        // "Agregar otro medio de pago", "Asignar cliente", dropzones: ocupa el
        // ancho del bloque y se lee como un lugar vacio donde falta algo.
        dashed:
          "w-full border border-dashed border-input text-muted-foreground hover:border-ring hover:text-foreground",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        // Mobile-first: alto tactil >= 44px, baja a densidad de escritorio en sm+.
        default: "h-11 px-4 sm:h-10",
        // `sm` mide 40 en el celular (antes 36): los botones chicos eran los
        // más tocados de pie, con una mano, y los agentes los marcaban como
        // blanco difícil. En desktop sigue en 32.
        sm: "h-10 px-3 text-xs sm:h-8 [&_svg]:size-3.5",
        lg: "h-12 px-6 sm:h-11",
        icon: "size-11 sm:size-10",
        // Boton de icono para filas de tabla y cabeceras de bloque: 36/32.
        "icon-sm": "size-10 sm:size-8 [&_svg]:size-3.5",
      },
    },
    // Un link no es una caja: sin alto fijo ni padding, se alinea con el texto.
    compoundVariants: [{ variant: "link", className: "h-auto px-0" }],
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  /**
   * Spinner + `disabled` + `aria-busy`. La etiqueta NO cambia: "Guardar" sigue
   * diciendo "Guardar" mientras guarda, asi el texto no salta y se termina la
   * mezcla de "Guardando" con tres puntos y con elipsis tipografica.
   */
  cargando?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, asChild = false, cargando = false, disabled, children, ...props },
    ref,
  ) => {
    const clases = cn(buttonVariants({ variant, size, className }));

    if (asChild) {
      // Slot exige un unico hijo, asi que no hay donde meter el spinner: el
      // estado se marca con aria-busy (que ademas bloquea el puntero) y listo.
      return (
        <Slot className={clases} ref={ref} aria-busy={cargando ? true : undefined} {...props}>
          {children}
        </Slot>
      );
    }

    return (
      <button
        className={clases}
        ref={ref}
        disabled={disabled || cargando}
        aria-busy={cargando ? true : undefined}
        {...props}
      >
        {cargando ? <Spinner /> : null}
        {children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { buttonVariants };
